import { NavLink, Table } from '@/components';
import {
  ProjectStepLookupState,
  useGetHomesLookupListQuery,
  useGetProjectDashboardQuery,
  useGetProjectStepsQuery,
} from '@/redux';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectHomeProgressCell } from './ProjectHomeProgressCell';
import { ProjectDashboardRow } from '@/core';
import { orderBy } from 'lodash';
import { Typography } from 'antd';
import { array, useFiltersFactory } from '@/utils';

interface Props {
  projectId: string;
  communityId: string;
}

function useRowsWithTotals(rows: ProjectDashboardRow[]) {
  return useMemo(
    () =>
      rows.map((row) => {
        const values = row.steps.map((s) => s.stepPercent ?? 0);
        const total =
          values.length > 0
            ? Math.round(values.reduce((a, b) => a + b, 0) / values.length)
            : 0;

        let totalTasksToComplete = 0;
        let totalItemsToAdd = 0;

        totalTasksToComplete += row.steps.reduce(
          (sum, s) => sum + (s.tasksToComplete ?? 0),
          0,
        );

        totalItemsToAdd += row.steps.reduce(
          (sum, s) => sum + (s.itemsToAdd ?? 0),
          0,
        );

        return {
          ...row,
          totalPercent: total,
          totalItemsToAdd: totalItemsToAdd,
          totalTasksToComplete: totalTasksToComplete,
        };
      }),
    [rows],
  );
}

function useColumns(
  communityId: string,
  projectId: string,
  steps: ProjectStepLookupState[],
) {
  const { t } = useTranslation();

  const filters = useFiltersFactory(array.empty<ProjectDashboardRow>());

  const { data: homes } = useGetHomesLookupListQuery({
    communityId,
    projectId,
  });

  return useMemo(() => {
    const baseColumn = {
      title: t('projects.dashboard.home'),
      dataIndex: 'homeName',
      key: 'homeName',
      width: 180,
      fixed: 'left' as const,
      ...filters.selectWithOptions(
        (x) => x.homeName,
        homes?.map((x) => x.name) ?? [],
        'equal',
      ),
      render: (_: any, row: ProjectDashboardRow) =>
        row.homeId === 'summary' ? (
          <Typography.Title level={5}>{row.homeName}</Typography.Title>
        ) : (
          <NavLink to={`/communities/${communityId}/homes/${row.homeId}`}>
            {row.homeName}
          </NavLink>
        ),
    };

    const stepColumns = steps.map((step) => ({
      title: step.name,
      dataIndex: step.id,
      key: step.id,
      width: 140,
      render: (_: any, row: ProjectDashboardRow) => {
        const cell = row.steps.find((s) => s.stepId === step.id);
        if (!cell) return '-';

        return (
          <ProjectHomeProgressCell
            percentage={cell.stepPercent}
            isFinalized={cell.isFinalized}
            deadline={cell.deadline}
            itemsToAdd={cell.itemsToAdd}
            tasksToComplete={cell.tasksToComplete}
            align={row.homeId === 'summary' ? undefined : 'center'}
            name={row.homeId === 'summary' ? step.name : undefined}
            size={row.homeId === 'summary' ? 'large' : undefined}
          />
        );
      },
    }));

    const totalColumn = {
      title: t('projects.dashboard.total'),
      key: 'totalPercent',
      width: 140,
      render: (_: any, row: ProjectDashboardRow) => (
        <ProjectHomeProgressCell
          percentage={(row as any).totalPercent}
          tasksToComplete={(row as any).totalTasksToComplete}
          itemsToAdd={(row as any).totalItemsToAdd}
          isFinalized={false}
          size={row.homeId === 'summary' ? 'large' : undefined}
          align="center"
          name={row.homeId === 'summary' ? '-' : undefined}
          noColor
        />
      ),
    };

    return [baseColumn, ...stepColumns, totalColumn];
  }, [communityId, filters, homes, steps, t]);
}

function useSummaryRow(
  rows: ProjectDashboardRow[],
  steps: ProjectStepLookupState[],
) {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!rows.length || !steps.length) return null;

    const summary: any = {
      homeId: 'summary',
      homeName: t('projects.dashboard.total'),
      steps: [],
    };

    const stepPercents: number[] = [];

    let totalTasksToComplete = 0;
    let totalItemsToAdd = 0;

    steps.forEach((step) => {
      const stepRows = rows
        .map((row) => row.steps.find((s) => s.stepId === step.id))
        .filter((s): s is NonNullable<typeof s> => !!s);

      const values = stepRows
        .map((s) => s.stepPercent)
        .filter((v): v is number => typeof v === 'number');

      const avg =
        values.length > 0
          ? Math.round(values.reduce((a, b) => a + b, 0) / values.length)
          : 0;

      const tasksToComplete = stepRows.reduce(
        (sum, s) => sum + (s.tasksToComplete ?? 0),
        0,
      );

      totalTasksToComplete += tasksToComplete;

      const itemsToAdd = stepRows.reduce(
        (sum, s) => sum + (s.itemsToAdd ?? 0),
        0,
      );
      totalItemsToAdd += itemsToAdd;

      summary.steps.push({
        stepId: step.id,
        stepPercent: avg,
        isFinalized: false,
        name: step.name,
        deadline: step.deadlineDate,
        itemsToAdd: itemsToAdd,
        tasksToComplete: tasksToComplete,
      });

      stepPercents.push(avg);
    });

    summary.totalPercent = Math.round(
      stepPercents.reduce((a, b) => a + b, 0) / stepPercents.length,
    );

    summary.totalTasksToComplete = totalTasksToComplete;
    summary.totalItemsToAdd = totalItemsToAdd;

    return summary as ProjectDashboardRow;
  }, [rows, steps, t]);
}

const useGetSteps = (projectId: string) => {
  const { data: steps = [] } = useGetProjectStepsQuery({ ids: projectId });
  return useMemo(() => {
    const orderedSteps = orderBy(steps, (x) => x.index);
    return orderedSteps;
  }, [steps]);
};

export const ProjectDashboardPanel = ({ projectId, communityId }: Props) => {
  const { data: rawRows = [] } = useGetProjectDashboardQuery({ id: projectId });
  const steps = useGetSteps(projectId);
  const rows = useRowsWithTotals(rawRows);
  const columns = useColumns(communityId, projectId, steps);
  const summaryRow = useSummaryRow(rows, steps);

  return (
    <>
      {summaryRow && (
        <Table<ProjectDashboardRow>
          columns={columns}
          dataSource={[summaryRow]}
          rowKey={(row) => row.homeId}
          pagination={false}
          name="project-dashboard-summary"
          hideDefaultActions
          showHeader={false}
        />
      )}
      <Table<ProjectDashboardRow>
        columns={columns}
        hideDefaultActions
        dataSource={rows}
        rowKey={(row) => row.homeId}
        pagination={false}
        name="project-dashboard"
      />
    </>
  );
};
